import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CheckCircle2, Loader2, Dog, Cat, PawPrint } from 'lucide-react';
import { useLang } from '@/lib/LangContext';

const API = process.env.REACT_APP_BACKEND_URL || '';

const PET_OPTIONS = [
  { value: 'dog', Icon: Dog, key: 'petTypeDog' },
  { value: 'cat', Icon: Cat, key: 'petTypeCat' },
  { value: 'other', Icon: PawPrint, key: 'petTypeOther' },
];

const EMPTY = {
  firstName: '', surname: '', phone: '', address: '',
  petType: 'dog', petName: '',
};

/**
 * Simplified public registration modal.
 *
 * Fields: Name, Surname, Phone Number, Address, Pet Type, Pet Name.
 * On submit we POST a lightweight lead to /api/quote-requests (reusing the
 * existing admin pipeline + email alert) and show a confirmation screen.
 */
export default function RegistrationModal({ open, onOpenChange }) {
  const { t } = useLang();
  const s = t.signup || {};
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  // Reset state whenever the modal is reopened.
  useEffect(() => {
    if (open) {
      setForm(EMPTY);
      setError('');
      setSubmitted(false);
      setSubmitting(false);
    }
  }, [open]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const canSubmit =
    form.firstName.trim() && form.surname.trim() && form.phone.trim() &&
    form.address.trim() && form.petName.trim();

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!canSubmit) {
      setError(s.errorRequired || 'Please fill in all required fields.');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        name: `${form.firstName.trim()} ${form.surname.trim()}`,
        phone: form.phone.trim(),
        pet_type: form.petType,
        pet_name: form.petName.trim(),
        city_or_area: form.address.trim(),
        allergies_notes: 'Registration via home popup',
        consent: true,
      };
      const res = await fetch(`${API}/api/quote-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('request failed');
      setSubmitted(true);
    } catch {
      setError(s.errorNetwork || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl p-0 overflow-hidden" data-testid="registration-modal">
        {submitted ? (
          <div className="p-8 text-center" data-testid="registration-thanks">
            <div className="mx-auto w-16 h-16 rounded-full bg-[#0f6e44]/10 text-[#0f6e44] flex items-center justify-center mb-4">
              <CheckCircle2 size={32} />
            </div>
            <h2 className="font-display font-extrabold text-[#05223D] text-2xl">
              {s.thanksTitle}
            </h2>
            <p className="mt-3 text-[#334155] leading-relaxed">
              {s.thanksBody}
            </p>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="btn-primary mt-6"
              data-testid="registration-thanks-close"
            >
              {s.thanksClose}
            </button>
          </div>
        ) : (
          <>
            <DialogHeader className="px-6 pt-6 pb-2 text-left">
              <DialogTitle className="font-display text-2xl text-[#05223D]">
                {s.title}
              </DialogTitle>
              <DialogDescription className="text-[#465B70]">
                {s.subtitle}
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={onSubmit} className="px-6 pb-6 pt-2 space-y-4" data-testid="registration-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="reg-firstName" className="text-[13px] font-semibold text-[#05223D]">
                    {s.nameLabel} <span className="text-[#F25C05]">*</span>
                  </Label>
                  <Input
                    id="reg-firstName"
                    data-testid="reg-first-name"
                    value={form.firstName}
                    onChange={(e) => update('firstName', e.target.value)}
                    placeholder={s.namePh}
                    autoComplete="given-name"
                    required
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="reg-surname" className="text-[13px] font-semibold text-[#05223D]">
                    {s.surnameLabel} <span className="text-[#F25C05]">*</span>
                  </Label>
                  <Input
                    id="reg-surname"
                    data-testid="reg-surname"
                    value={form.surname}
                    onChange={(e) => update('surname', e.target.value)}
                    placeholder={s.surnamePh}
                    autoComplete="family-name"
                    required
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="reg-phone" className="text-[13px] font-semibold text-[#05223D]">
                    {s.phoneLabel} <span className="text-[#F25C05]">*</span>
                  </Label>
                  <Input
                    id="reg-phone"
                    data-testid="reg-phone"
                    type="tel"
                    value={form.phone}
                    onChange={(e) => update('phone', e.target.value)}
                    placeholder={s.phonePh}
                    autoComplete="tel"
                    required
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="reg-address" className="text-[13px] font-semibold text-[#05223D]">
                    {s.addressLabel} <span className="text-[#F25C05]">*</span>
                  </Label>
                  <Input
                    id="reg-address"
                    data-testid="reg-address"
                    value={form.address}
                    onChange={(e) => update('address', e.target.value)}
                    placeholder={s.addressPh}
                    autoComplete="street-address"
                    required
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-[13px] font-semibold text-[#05223D]">
                  {s.petTypeLabel}
                </Label>
                <div className="grid grid-cols-3 gap-2" role="radiogroup" aria-label={s.petTypeLabel}>
                  {PET_OPTIONS.map(({ value, Icon, key }) => {
                    const selected = form.petType === value;
                    return (
                      <button
                        key={value}
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        onClick={() => update('petType', value)}
                        data-testid={`reg-pet-type-${value}`}
                        className={`inline-flex items-center justify-center gap-2 py-2.5 rounded-xl border font-semibold text-sm transition-colors ${
                          selected
                            ? 'bg-[#0A4D8C] text-white border-[#0A4D8C]'
                            : 'bg-white text-[#05223D] border-[#eadfce] hover:border-[#0A4D8C]/60'
                        }`}
                      >
                        <Icon size={16} />
                        {s[key]}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="reg-petName" className="text-[13px] font-semibold text-[#05223D]">
                  {s.petNameLabel} <span className="text-[#F25C05]">*</span>
                </Label>
                <Input
                  id="reg-petName"
                  data-testid="reg-pet-name"
                  value={form.petName}
                  onChange={(e) => update('petName', e.target.value)}
                  placeholder={s.petNamePh}
                  required
                />
              </div>

              {error && (
                <p className="text-sm font-semibold text-[#B91C1C] bg-[#B91C1C]/5 border border-[#B91C1C]/15 px-3 py-2 rounded-xl" data-testid="registration-error">
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={submitting}
                data-testid="registration-submit"
                className="btn-primary w-full justify-center disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {submitting && <Loader2 size={16} className="animate-spin" />}
                {submitting ? s.sending : s.submit}
              </button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
